import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiEnvelope, ApiUser } from '../api/api.types';
import { CsrfService } from './csrf.service';

function normalizeUser(raw: ApiUser | null): ApiUser | null {
  if (!raw) return null;
  return {
    ...raw,
    email: typeof raw.email === 'string' ? raw.email.trim() : raw.email,
    permissions: raw.permissions ?? [],
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly csrf = inject(CsrfService);

  readonly user = signal<ApiUser | null>(null);
  readonly ready = signal(false);

  isEmailVerified(user: ApiUser | null = this.user()): boolean {
    return !!user?.email_verified_at;
  }

  isProfileComplete(user: ApiUser | null = this.user()): boolean {
    if (!user) return false;
    return !!(user.name && user.phone && user.profile?.address);
  }

  verifyEmailUrl(user: ApiUser): string {
    const params = new URLSearchParams({
      id: String(user.id),
      email: user.email,
    });
    return `${environment.authUrl}/verify-email?${params.toString()}`;
  }

  ensureSession(): Observable<ApiUser | null> {
    return this.csrf.prime().pipe(
      switchMap(() => this.fetchUser()),
      switchMap((user) => {
        if (user || !environment.devAutoLogin) {
          return of(user);
        }
        return this.login(environment.devLogin.login, environment.devLogin.password);
      }),
      tap((user) => {
        this.user.set(user);
        this.ready.set(true);
      }),
      catchError(() => {
        this.user.set(null);
        this.ready.set(true);
        return of(null);
      })
    );
  }

  login(login: string, password: string): Observable<ApiUser | null> {
    return this.csrf.prime().pipe(
      switchMap(() =>
        this.http.post<ApiEnvelope<ApiUser>>(`${environment.apiUrl}/auth/login`, {
          login,
          password,
        })
      ),
      map((res) => normalizeUser(res.data ?? null)),
      tap((user) => this.user.set(user))
    );
  }

  updateProfile(data: Record<string, any>): Observable<ApiUser | null> {
    return this.http.patch<ApiEnvelope<ApiUser>>(`${environment.apiUrl}/auth/me`, data).pipe(
      map((res) => normalizeUser(res.data ?? null)),
      tap((user) => {
        if (user) {
          this.user.set(user);
        }
      })
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/auth/logout`, {}).pipe(
      tap(() => this.user.set(null)),
      catchError(() => {
        this.user.set(null);
        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  /** Prefer auth/me (UserResource + roles/permissions) over the raw /user model. */
  private fetchUser(): Observable<ApiUser | null> {
    return this.http.get<ApiEnvelope<ApiUser>>(`${environment.apiUrl}/auth/me`).pipe(
      map((res) => normalizeUser(res.data ?? null)),
      catchError(() =>
        this.http.get<ApiUser>(`${environment.apiUrl}/user`).pipe(
          map((u) => normalizeUser(u)),
          catchError(() => of(null))
        )
      )
    );
  }
}
